import { format, isValid } from 'date-fns';
import { ISearchCriteria } from '../api/queries';

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const formatDateWithoutTimeUTC = (
  dateOrDateString: Date | string
): string => {
  const date = new Date(dateOrDateString);

  // We store deadlines as the instant when a deadline becomes overdue -
  // defaulting to the first second of the next day, immediately after midnight.
  // For display purposes, we want to subtract a minute,
  // to get the actual deadline, not the first instance when it's overdue.

  // Subtract one minute to get actual deadline
  const deadlineDate = new Date(date.getTime() - 1000 * 60);

  const deadlineParts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).formatToParts(deadlineDate);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    deadlineParts.find(item => item.type === type)?.value;
  const deadlineString = `${part('month')} ${part('day')}, ${part('year')}`;

  return deadlineString;
};

export const formatDeadlineDate = (dateOrDateString: Date | string): string => {
  const date = new Date(dateOrDateString);

  // We store deadlines as the instant when a deadline becomes overdue -
  // defaulting to the first second of the next day, immediately after midnight.
  // For display purposes, we want to subtract a minute,
  // to get the actual deadline, not the first instance when it's overdue.

  // Subtract one minute to get actual deadline
  const deadlineDate = new Date(date.getTime() - 1000 * 60);

  const deadlineString = deadlineDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC'
  });

  return `${deadlineString} UTC`;
};

export const timeFormatDeadlineDate = (
  dateOrDateString: Date | string
): string => {
  const date = new Date(dateOrDateString);

  // We store deadlines as the instant when a deadline becomes overdue -
  // defaulting to the first second of the next day, immediately after midnight.
  // For display purposes, we want to subtract a minute,
  // to get the actual deadline, not the first instance when it's overdue.

  // Subtract one minute to get actual deadline
  const deadlineDate = new Date(date.getTime() - 1000 * 60);

  const deadlineParts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  }).formatToParts(deadlineDate);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    deadlineParts.find(item => item.type === type)?.value;
  const deadlineString = `${part('month')} ${part('day')} ${part(
    'year'
  )}, ${part('hour')}:${part('minute')}`;

  return `${deadlineString} UTC`;
};
export const formatToUTCDate = (dateOrDateString: Date | string): string => {
  const date = new Date(dateOrDateString);

  const dateString = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC'
  });

  return `${dateString} UTC`;
};
export const formatDateSafe = (value: Date | string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (!isValid(date)) {
    return '';
  }
  return format(date, 'yyyy-MM-dd');
};
export const toUTCProposedDateString = (dateOrDateString: Date | string) => {
  const date = new Date(dateOrDateString);
  const deadlineDate = new Date(date.getTime());

  return deadlineDate.toLocaleDateString('en-CA', { timeZone: 'UTC' });
};

export const toUTCDateString = (dateOrDateString: Date | string) => {
  const date = new Date(dateOrDateString);
  const deadlineDate = new Date(date.getTime() - 1000 * 60);

  return deadlineDate.toLocaleDateString('en-CA', { timeZone: 'UTC' });
};

export const DATE_FILTER_PROPERTY_MAP: Record<
  string,
  [string, string, string, string]
> = {
  '1 Month': [
    'acceptedCount1Month',
    'reviewsCompleted1Month',
    'acceptedNoReview1Month',
    'manuscriptHistory1Month'
  ],
  '3 Months': [
    'acceptedCount3Month',
    'reviewsCompleted3Month',
    'acceptedNoReview3Month',
    'manuscriptHistory3Month'
  ],
  '6 Months': [
    'acceptedCount6Month',
    'reviewsCompleted6Month',
    'acceptedNoReview6Month',
    'manuscriptHistory6Month'
  ],
  '1 Year': [
    'acceptedCount12Month',
    'reviewsCompleted12Month',
    'acceptedNoReview12Month',
    'manuscriptHistory12Month'
  ],
  '2 Years': [
    'acceptedCount24Month',
    'reviewsCompleted24Month',
    'acceptedNoReview24Month',
    'manuscriptHistory24Month'
  ]
};

export const getDateFilterCriteria = (
  dateValue: string,
  reviewerProperties: any,
  dateFilters?: { key: string; value: string[]; label: string }[]
): ISearchCriteria[] => {
  if (!dateValue || dateValue === 'All') return [];

  if (dateFilters) {
    const filter = dateFilters.find(f => f.key === dateValue);
    if (!filter) return [];

    const criteria: (ISearchCriteria | null)[] = filter.value.map(label => {
      const schemaId = reviewerProperties?.[0]?.properties.find(
        (p: any) => p.predicateLabel === label
      )?.id;

      if (!schemaId) return null;

      return {
        id: schemaId,
        value: ['1', '10000'],
        operator: 'OR' as const,
        datatype: 'xsd:integer'
      };
    });

    return criteria.filter((c): c is ISearchCriteria => c !== null);
  }

  const props = DATE_FILTER_PROPERTY_MAP[dateValue];
  if (!props) return [];

  const criteria: (ISearchCriteria | null)[] = props.map(label => {
    const schemaId = reviewerProperties?.[0]?.properties.find(
      (p: any) => p.predicateLabel === label
    )?.id;

    if (!schemaId) return null;

    return {
      id: schemaId,
      value: ['1', '10000'],
      operator: 'OR' as const,
      datatype: 'xsd:integer'
    };
  });

  // Explicitly cast after filtering
  return criteria.filter((c): c is ISearchCriteria => c !== null);
};

export const formatDateAndTime = (date: Date | string): string => {
  return format(new Date(date), "MMM dd, yyyy 'at' HH:mm"); // Example: Jan 01, 2024 at 14:30
};
